import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Icoon } from '../ui/Icoon';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { addDagen, fmtDag, fmtUren, maandagVan, verschilDagen } from '@ep/core';
import { useData, useMag, type TaakRij } from '../lib/data';
import { api } from '../lib/api';
import { toast } from '../ui/toast';
import { useBaselines, useProjectStand, verschilTekst } from '../ui/Baseline';
import { urenVan } from '../ui/TakenTabel';
import { Laden } from '../ui/ui';
import { ProjectTijdlijn } from '../ui/ProjectTijdlijn';

interface Regel { wpId?: string; niveau: 0 | 1 | 2; wbs: string; naam: string; start?: string | null; eind?: string | null; uren: number; resource?: string; deadline?: string | null; samenvatting?: boolean }

/** Projectplanning in MS Project-stijl: WBS-tabel met balken, geschikt om af te drukken of als PDF te bewaren. */
export function Projectplanning() {
  const { id } = useParams();
  const { data: d } = useData();
  const [zp, setZp] = useSearchParams();
  const weergave = zp.get('weergave') === 'tijdlijn' ? 'tijdlijn' : 'gantt';
  const p = d?.projecten.find((x) => x.id === id);
  const { regels, start, dagen } = useMemo(() => {
    if (!d || !p) return { regels: [] as Regel[], start: '', dagen: 0 };
    const taken = d.taken.filter((t) => t.projectId === p.id);
    const eng = (t: TaakRij) => d.engineers.find((e) => e.id === t.engineerId)?.naam ?? '–';
    const r: Regel[] = [];
    const span = (xs: TaakRij[]) => {
      const s = xs.map((t) => t.startdatum).filter(Boolean).sort() as string[];
      const e = xs.map((t) => t.einddatum ?? t.startdatum).filter(Boolean).sort() as string[];
      return { start: s[0] ?? null, eind: e.slice(-1)[0] ?? null, uren: xs.reduce((a, t) => a + urenVan(t), 0) };
    };
    const wps = d.werkpakketten.filter((w) => w.projectId === p.id).sort((a, b) => a.volgorde - b.volgorde);
    const tps = d.takenpakketten.filter((t) => t.projectId === p.id);
    wps.forEach((w, i) => {
      const wTps = tps.filter((t) => t.werkpakketId === w.id).sort((a, b) => a.volgorde - b.volgorde);
      const wTaken = taken.filter((t) => t.werkpakketId === w.id || wTps.some((x) => x.id === t.takenpakketId));
      r.push({ wpId: w.id, niveau: 0, wbs: `${i + 1}`, naam: w.naam, ...span(wTaken), deadline: w.deadline, samenvatting: true });
      wTps.forEach((tp, j) => {
        const tk = taken.filter((t) => t.takenpakketId === tp.id);
        r.push({ niveau: 1, wbs: `${i + 1}.${j + 1}`, naam: `${tp.naam}${tp.expertise ? ` (${tp.expertise})` : ''}`, ...span(tk), uren: tp.uren, samenvatting: true });
        tk.forEach((t, k) => r.push({ niveau: 2, wbs: `${i + 1}.${j + 1}.${k + 1}`, naam: t.titel, start: t.startdatum, eind: t.einddatum, uren: urenVan(t), resource: eng(t) }));
      });
      taken.filter((t) => t.werkpakketId === w.id && !t.takenpakketId).forEach((t, k) => r.push({ niveau: 1, wbs: `${i + 1}.${wTps.length + k + 1}`, naam: t.titel, start: t.startdatum, eind: t.einddatum, uren: urenVan(t), resource: eng(t) }));
    });
    const los = taken.filter((t) => !t.werkpakketId && !t.takenpakketId);
    if (los.length) {
      r.push({ niveau: 0, wbs: `${wps.length + 1}`, naam: 'Overige taken', ...span(los), samenvatting: true });
      los.forEach((t, k) => r.push({ niveau: 1, wbs: `${wps.length + 1}.${k + 1}`, naam: t.titel, start: t.startdatum, eind: t.einddatum, uren: urenVan(t), resource: eng(t) }));
    }
    const datums = [p.start, p.livegang, d.vandaag, ...r.flatMap((x) => [x.start, x.eind, x.deadline])].filter(Boolean).sort() as string[];
    const s = maandagVan(datums[0]);
    return { regels: r, start: s, dagen: Math.max(28, verschilDagen(s, addDagen(datums.slice(-1)[0], 7))) };
  }, [d, p]);
  // SPEC §20: baselines en kritiek pad
  const mag = useMag();
  const qc = useQueryClient();
  const bls = useBaselines(id).data ?? [];
  const [blId, setBlId] = useState<string | null>(null);
  const bl = bls.find((b) => b.id === blId) ?? bls[0] ?? null;
  const stand = useProjectStand(d, id, bl);
  const [bezig, setBezig] = useState(false);
  const baselineActie = async (f: () => Promise<unknown>, melding: string) => {
    setBezig(true);
    try {
      await f();
      await qc.invalidateQueries({ queryKey: ['baselines', id] });
      setBlId(null);
      toast(melding, 'succes');
    } catch (e) {
      toast((e as Error).message, 'fout');
    } finally {
      setBezig(false);
    }
  };
  if (!d) return <Laden />;
  if (!p) return <div className="pagina paneel">Project niet gevonden</div>;
  const pct = (x: string) => (verschilDagen(start, x) / dagen) * 100;
  const weken: string[] = [];
  for (let w = start; verschilDagen(start, w) < dagen; w = addDagen(w, 7)) weken.push(w);
  const n = Math.ceil(weken.length / 20);
  return (
    <div className="pagina">
      <div className="rij geen-print" style={{ marginBottom: 12 }}>
        <Link className="knop" to={`/projecten/${p.id}`}><Icoon naam="arrow-left" maat={16} /> {p.nummer}</Link>
        <div className="groei" />
        <div className="rij" style={{ gap: 0 }}>
          <button className={`knop${weergave === 'gantt' ? ' primair' : ''}`} style={{ borderRadius: '6px 0 0 6px' }} onClick={() => setZp({}, { replace: true })}><Icoon naam="list-checks" maat={16} /> Gantt-tabel</button>
          <button className={`knop${weergave === 'tijdlijn' ? ' primair' : ''}`} style={{ borderRadius: '0 6px 6px 0', marginLeft: -1 }} onClick={() => setZp({ weergave: 'tijdlijn' }, { replace: true })}><Icoon naam="chart-gantt" maat={16} /> Tijdlijn</button>
        </div>
        <button className="knop primair" onClick={() => window.print()}><Icoon naam="printer" maat={16} /> Afdrukken / PDF</button>
      </div>
      <div className="paneel">
        <h1>Projectplanning {p.nummer} · {p.naam}</h1>
        <div className="mute klein" style={{ marginBottom: 12 }}>
          {p.klant} · PM {p.pmNaam ?? '–'} · start {fmtDag(p.start, 'jaar') || '–'} · livegang {fmtDag(p.livegang, 'jaar') || '–'} · afgedrukt {fmtDag(d.vandaag, 'jaar')}
        </div>
        {weergave === 'gantt' && stand?.einde && (
          <div className="rij wrap" style={{ marginBottom: 10, gap: 10 }}>
            <span className="klein">
              Klaar volgens planning: <b>{fmtDag(stand.einde, 'jaar')}</b>
              {stand.buffer != null && <> · {stand.buffer >= 0 ? `${stand.buffer} werkdagen buffer tot de livegang` : <span className="verschil-later">{-stand.buffer} werkdagen na de livegang</span>}</>}
              {bl && stand.verschil != null && <> · t.o.v. {bl.naam}: <span className={stand.verschil > 0 ? 'verschil-later' : stand.verschil < 0 ? 'verschil-eerder' : ''}>{verschilTekst(stand.verschil)}</span></>}
            </span>
            <div className="groei" />
            <span className="geen-print rij" style={{ gap: 6 }}>
              {bls.length > 0 && (
                <select value={bl?.id ?? ''} onChange={(e) => setBlId(e.target.value)} style={{ width: 'auto' }} aria-label="Vergelijk met baseline">
                  {bls.map((b) => <option key={b.id} value={b.id}>{b.naam} · {b.door}</option>)}
                </select>
              )}
              {mag('pakketten.schrijven') && (
                <>
                  <button className="knop klein" disabled={bezig} onClick={() => void baselineActie(() => api.post(`/api/projecten/${p.id}/baselines`, {}), 'Baseline vastgelegd')}><Icoon naam="flag" maat={14} /> Baseline vastleggen</button>
                  {bl && <button className="knop klein" disabled={bezig} onClick={() => window.confirm(`${bl.naam} verwijderen?`) && void baselineActie(() => api.del(`/api/baselines/${bl.id}`), 'Baseline verwijderd')} title="Deze baseline verwijderen"><Icoon naam="trash-2" maat={14} /></button>}
                </>
              )}
            </span>
          </div>
        )}
        {weergave === 'tijdlijn' ? (
          <div style={{ overflowX: 'auto' }}>
            <ProjectTijdlijn d={d} p={p} />
          </div>
        ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="tabel" style={{ tableLayout: 'fixed', minWidth: 1240 }}>
            <colgroup><col width={52} /><col width={230} /><col width={64} /><col width={64} /><col width={48} /><col width={110} /><col width={62} /><col width={70} /><col /></colgroup>
            <thead>
              <tr>
                <th>WBS</th><th>Taaknaam</th><th>Start</th><th>Eind</th><th className="rechts">Uren</th><th>Resource</th><th title="Werkdagen dat het werkpakket later klaar kan zijn zonder het project op te houden">Speling</th><th title="Verschil in het eind t.o.v. de gekozen baseline">Baseline</th>
                <th style={{ position: 'relative', padding: 0 }}>
                  <div className="gantt-schaal" style={{ height: 30 }}>
                    {weken.map((w, i) => (i % n === 0 ? <div key={w} className="tik" style={{ left: `${pct(w)}%`, paddingTop: 8 }}>{fmtDag(w, 'datum')}</div> : null))}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {regels.map((r) => {
                const w = r.wpId ? stand?.perWp.get(r.wpId) : undefined;
                const kritiek = !!w?.speling?.kritiek;
                return (
                <tr key={r.wbs}>
                  <td className="klein" style={{ padding: '6px 8px' }}>{r.wbs}</td>
                  <td style={{ padding: '6px 8px', paddingLeft: 8 + r.niveau * 14, fontWeight: r.samenvatting ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.naam}>{r.naam}</td>
                  <td className="klein" style={{ padding: '6px 8px' }}>{fmtDag(r.start, 'datum')}</td>
                  <td className="klein" style={{ padding: '6px 8px' }}>{fmtDag(r.eind, 'datum')}</td>
                  <td className="klein rechts" style={{ padding: '6px 8px' }}>{fmtUren(r.uren)}</td>
                  <td className="klein" style={{ padding: '6px 8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.resource ?? ''}</td>
                  <td className={`klein ${kritiek ? 'speling-0' : ''}`} style={{ padding: '6px 8px' }} title={kritiek ? 'Kritiek pad: elke dag vertraging schuift het project op' : undefined}>{w?.speling ? (kritiek ? 'kritiek' : `${w.speling.speling} wd`) : ''}</td>
                  <td className={`klein ${w?.verschil ? (w.verschil > 0 ? 'verschil-later' : 'verschil-eerder') : ''}`} style={{ padding: '6px 8px' }} title={w?.baseline?.eind ? `Baseline: eind ${fmtDag(w.baseline.eind, 'jaar')}` : undefined}>{w ? verschilTekst(w.verschil) : ''}</td>
                  <td style={{ position: 'relative', padding: 0 }}>
                    <div className="gantt-lijn" style={{ left: `${pct(d.vandaag)}%` }} />
                    {p.livegang && <div className="gantt-lijn live" style={{ left: `${pct(p.livegang)}%` }} />}
                    {w?.baseline?.start && w.baseline.eind && (
                      <div className="gantt-staaf baseline" style={{ left: `${pct(w.baseline.start)}%`, width: `${((verschilDagen(w.baseline.start, w.baseline.eind) + 1) / dagen) * 100}%` }} title={`Baseline: ${fmtDag(w.baseline.start)} – ${fmtDag(w.baseline.eind)}`} />
                    )}
                    {r.start && r.eind && (
                      <div
                        className={`gantt-staaf${kritiek ? ' kritiek' : ''}`}
                        style={{ left: `${pct(r.start)}%`, width: `${((verschilDagen(r.start, r.eind) + 1) / dagen) * 100}%`, background: r.samenvatting ? 'var(--ink)' : 'var(--accent)', height: r.samenvatting ? 8 : 14, top: r.samenvatting ? 'calc(50% - 4px)' : 'calc(50% - 7px)' }}
                        title={`${r.naam}: ${fmtDag(r.start)} – ${fmtDag(r.eind)}`}
                      />
                    )}
                    {r.deadline && <div className="gantt-ruit deadline" style={{ left: `calc(${pct(r.deadline)}% - 6px)`, top: 'calc(50% - 6px)' }} title={`Deadline ${fmtDag(r.deadline, 'jaar')}`} />}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
        {weergave === 'gantt' && !regels.length && <div className="leeg">Nog geen werkpakketten of taken.</div>}
        {weergave === 'gantt' && stand?.einde && <div className="klein mute" style={{ marginTop: 8 }}>Rood = kritiek pad (geen speling) · grijze lijn = baseline · speling en verschil in werkdagen (wd).</div>}
      </div>
    </div>
  );
}
